'use client';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useDebounce } from '@/hooks/useDebounce';
import { createHighlightProps, hasHighlighting } from '@/lib/highlight-utils';
import { useCurrentTenant } from '@/lib/tenant';
import { cn, getRootLink } from '@/lib/utils';
import {
  checkEnhancedSearchStatus,
  enhancedSearchQuestions,
  getEnhancedSuggestions,
} from '@/services/api/enhanced-search';
import { searchQuestions } from '@/services/api/question';
import { QuestionInfoType } from '@/types/api/question';
import { StoryInfoType } from '@/types/api/stories';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@radix-ui/react-tooltip';
import {
  ArrowRight,
  Loader2,
  PlusCircle,
  Search,
  Sparkles,
} from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Helper to order questions first while preserving original relative order
function orderQuestionsFirst(
  results: (QuestionInfoType | StoryInfoType)[]
): (QuestionInfoType | StoryInfoType)[] {
  const questions = results.filter(
    (r) => !('type' in r && (r as any).type === 'story')
  );
  const stories = results.filter(
    (r) => 'type' in r && (r as any).type === 'story'
  );
  return [...questions, ...stories];
}

interface SearchNavbarProps {
  isSearchOpen: boolean;
  setIsSearchOpen: (open: boolean) => void;
}

// ✅ Type guard for story results
interface StoryResult extends StoryInfoType {
  type: 'story';
}

const isStoryResult = (
  result: QuestionInfoType | StoryInfoType
): result is StoryResult => {
  return 'type' in result && (result as any).type === 'story';
};

export function SearchNavbar({
  isSearchOpen,
  setIsSearchOpen,
}: SearchNavbarProps) {
  const [searchValue, setSearchValue] = useState('');
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [searchResults, setSearchResults] = useState<
    (QuestionInfoType | StoryInfoType)[]
  >([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchSource, setSearchSource] = useState<
    'elasticsearch' | 'traditional'
  >('traditional');
  const [isEnhancedAvailable, setIsEnhancedAvailable] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const { hasValidTenant, isLoading: tenantLoading } = useCurrentTenant();

  const debouncedSearchValue = useDebounce(searchValue, 500);

  // Utility: build a short, plain-text preview from rich HTML (without links, URLs, or leftover HTML)
  const buildDescriptionPreview = (
    html: string | null | undefined,
    maxLength = 200
  ): string => {
    if (!html) return '';

    // 1) Decode common HTML entities so encoded tags become real tags
    let decoded = html
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#x27;/g, "'");

    // 2) Remove full <a ...> ... </a> blocks (including link text)
    decoded = decoded.replace(/<a\b[^>]*>.*?<\/a>/gis, '');

    // 3) Strip ANY remaining HTML tags (spans, divs, classes, inline styles, etc.)
    let text = decoded.replace(/<[^>]+>/g, ' ').trim();
    if (!text) return '';

    // 4) Remove bare URLs (http/https and www.*) from plain text
    text = text
      .replace(/https?:\/\/\S+/gi, '')
      .replace(/\bwww\.\S+/gi, '')
      .replace(/\S*:\/\/\S*/gi, '');

    // 5) Remove any attribute-like leftovers (href=..., class=..., target=..., rel=...)
    text = text
      .replace(/\bhref\s*=\s*["'][^"']*["']/gi, '')
      .replace(/\bclass\s*=\s*["'][^"']*["']/gi, '')
      .replace(/\btarget\s*=\s*["'][^"']*["']/gi, '')
      .replace(/\brel\s*=\s*["'][^"']*["']/gi, '')
      .replace(/\bhref\s*=\s*\S+/gi, '')
      .replace(/\bclass\s*=\s*\S+/gi, '')
      .replace(/\btarget\s*=\s*\S+/gi, '')
      .replace(/\brel\s*=\s*\S+/gi, '');

    // 6) Normalize whitespace
    text = text.replace(/\s+/g, ' ').trim();
    if (!text) return '';

    // 7) Truncate to maxLength, cutting at a word boundary
    if (text.length <= maxLength) return text;
    const truncated = text.slice(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');
    const safeCut = lastSpace > 0 ? truncated.slice(0, lastSpace) : truncated;
    return safeCut + '…';
  };

  // Check if enhanced search is available on mount - with caching
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const status = await checkEnhancedSearchStatus();
        setIsEnhancedAvailable(status.available);
      } catch (error) {
        // Fallback to traditional search on error
        setIsEnhancedAvailable(false);
      }
    };

    checkStatus();
  }, []);

  // Enhanced search with fallback - optimized with useCallback
  const performSearch = useCallback(
    async (searchValue: string) => {
      if (!searchValue) {
        setPopoverOpen(false);
        setSuggestions([]);
        return;
      }

      setIsLoading(true);
      setSearchResults([]);

      try {
        // Try enhanced search first if tenant is valid
        if (isEnhancedAvailable) {
          const res = await enhancedSearchQuestions({
            search: searchValue,
            page: 1,
            limit: 5,
          });
          setSearchResults(orderQuestionsFirst(res.data));
          setSearchSource(res.source);
        } else {
          // Use traditional search
          const res = await searchQuestions({
            search: searchValue,
            page: 1,
            limit: 5,
          });
          setSearchResults(orderQuestionsFirst(res.data));
          setSearchSource('traditional');
        }
      } catch (err) {
        // Fallback to traditional search on error
        try {
          const res = await searchQuestions({
            search: searchValue,
            page: 1,
            limit: 5,
          });
          setSearchResults(orderQuestionsFirst(res.data));
          setSearchSource('traditional');
        } catch (fallbackErr) {
          console.error(
            'Both enhanced and traditional search failed:',
            fallbackErr
          );
          setSearchResults([]);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [isEnhancedAvailable]
  );

  // Enhanced search with fallback
  useEffect(() => {
    performSearch(debouncedSearchValue);
  }, [debouncedSearchValue, performSearch]);

  // Get suggestions for enhanced search - optimized with useCallback
  const fetchSuggestions = useCallback(
    async (searchValue: string) => {
      if (searchValue && searchValue.length >= 2 && isEnhancedAvailable) {
        try {
          const res = await getEnhancedSuggestions(searchValue, 3);
          setSuggestions(res.suggestions);
        } catch (error) {
          setSuggestions([]);
        }
      } else {
        setSuggestions([]);
      }
    },
    [isEnhancedAvailable]
  );

  // Get suggestions for enhanced search
  useEffect(() => {
    fetchSuggestions(debouncedSearchValue);
  }, [debouncedSearchValue, fetchSuggestions]);

  // Open input on button click - optimized
  const handleSearchClick = useCallback(() => {
    setIsSearchOpen(true);
    // Use requestAnimationFrame for better performance
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  }, []);

  // Open popover when input is focused or has value - optimized
  const handleInputFocus = useCallback(() => {
    setPopoverOpen(true);
  }, []);

  // Close popover only if click is outside both input and popover
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        !inputRef.current?.contains(event.target as Node) &&
        !popoverRef.current?.contains(event.target as Node)
      ) {
        setPopoverOpen(false);
        setIsSearchOpen(false);
        setSearchValue('');
      }
    }
    if (popoverOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [popoverOpen, setIsSearchOpen]);

  // Open popover if input has value
  useEffect(() => {
    if (isSearchOpen && searchValue) {
      setPopoverOpen(true);
    } else if (!searchValue) {
      setPopoverOpen(false);
    }
  }, [isSearchOpen, searchValue]);

  // ✅ FIXED: Proper type guard handling for navigation with slug validation - optimized
  const handleSearchResultClick = useCallback(
    (result: QuestionInfoType | StoryInfoType) => {
      if (isStoryResult(result)) {
        // Navigate to story detail page - TypeScript now knows this is a StoryResult
        if (
          result.questionId &&
          result.question?.slug &&
          result.question.slug.trim() !== ''
        ) {
          // Story's question object doesn't include questionType, so use default 'NORMAL'
          const rootLink = getRootLink('NORMAL');
          debugger;
          router.push(
            `/${rootLink}/question/${result.question.slug}?focusedStoryId=${result.id}`
          );
        } else {
          console.warn(
            'Story navigation failed: Missing or empty question slug',
            result
          );
          // Optionally show a toast notification: "Unable to navigate to this story"
        }
      } else {
        // Navigate to question detail page with slug validation
        const question = result as QuestionInfoType;

        // ✅ CRITICAL FIX: Validate slug exists and is not empty
        if (question.slug && question.slug.trim() !== '') {
          const rootLink = getRootLink(question.questionType);
          router.push(`/${rootLink}/question/${question.slug}`);
        } else {
          console.warn(
            'Question navigation failed: Missing or empty slug',
            question
          );
          // Optionally show a toast notification: "Unable to navigate to this question"

          // ✅ FALLBACK: Try using question ID if available
          if (question.id) {
            // You could implement an ID-based route or redirect to search results
            router.push(
              `/search?q=${encodeURIComponent(question.questionTitle)}`
            );
          }
        }
      }
      setIsSearchOpen(false);
      setPopoverOpen(false);
    },
    [router]
  );

  const handleSuggestionClick = useCallback(
    (suggestion: string) => {
      setSearchValue(suggestion);
      router.push(`/search?q=${suggestion}`);
      setIsSearchOpen(false);
      setPopoverOpen(false);
    },
    [router]
  );

  return (
    <div
      ref={containerRef}
      className={cn(
        'flex items-center gap-3 justify-center',
        isSearchOpen ? 'w-[400px]' : 'auto'
      )}
    >
      {!isSearchOpen && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              {/* <Button
                variant="ghost"
                size="icon"
                id="search"
                type="button"
                aria-label="Search"
                className="text-[#00FFFB] border border-[#00FFFB] hover:bg-[#00FFFB]/20 transition-all duration-300"
                onClick={handleSearchClick}
              >
                <Search className="h-6 w-6" />
              </Button> */}
              <div className="relative w-full">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                <Input
                  onClick={handleSearchClick}
                  ref={inputRef}
                  type="text"
                  placeholder="Search"
                  className="bg-white  text-black placeholder:text-black focus-visible:border-[#00FFFB] h-10 pl-10 pr-4 w-full"
                  aria-label="Search"
                  autoComplete="off"
                />
              </div>
            </TooltipTrigger>
            <TooltipContent className="bg-white text-black p-2 rounded-md text-sm">
              Search
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      {isSearchOpen && (
        <div className="relative w-full">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#00FFFB] pointer-events-none" />
          <Input
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                router.push(`/search?q=${searchValue}`);
                setIsSearchOpen(false);
              }
            }}
            ref={inputRef}
            type="text"
            placeholder="Search"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onFocus={handleInputFocus}
            className="bg-ihs-dark-blue border border-[#00FFFB] text-white placeholder:text-[#00FFFB] focus-visible:border-[#00FFFB] h-10 pl-12 pr-12 w-full transition-all duration-300 ease-out"
            aria-label="Search"
            autoComplete="off"
          />
          <Button
            type="button"
            size="icon"
            className="absolute right-1 top-1/2 -translate-y-1/2 text-white bg-[#2563eb] rounded-full p-2 hover:bg-[#1d4ed8] transition"
            aria-label="Send"
            tabIndex={-1}
          >
            <ArrowRight className="w-5 h-5" />
          </Button>

          {/* ✅ FIXED: Show regular search results */}
          {popoverOpen && searchValue && (
            <div
              ref={popoverRef}
              className="absolute left-0 top-full z-50 w-[420px] p-0 mt-2 bg-popover text-popover-foreground rounded-md border shadow-md"
            >
              {/* Suggestions Section */}
              {suggestions.length > 0 && (
                <div className="border-b">
                  <div className="p-2 bg-gray-50 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Suggestions
                  </div>
                  {suggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      className="w-full text-left p-3 hover:bg-blue-50 border-b last:border-b-0 transition-colors"
                      onClick={() => handleSuggestionClick(suggestion)}
                    >
                      <div className="flex items-center gap-3">
                        <Search className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-800">{suggestion}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Results Header */}
              <div
                className="p-4 border-b font-semibold text-base text-gray-700 cursor-pointer hover:bg-gray-50"
                onClick={() => {
                  router.push(`/search?q=${searchValue}`);
                  setIsSearchOpen(false);
                  setPopoverOpen(false);
                }}
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Searching...</span>
                  </div>
                ) : (
                  <>
                    Search Results for "
                    <span className="text-ihs-blue">{searchValue}</span>"
                    {searchResults.length > 0 && (
                      <span className="text-sm text-gray-500 ml-2">
                        ({searchResults.length} found)
                      </span>
                    )}
                  </>
                )}
              </div>

              {/* Results List */}
              <div className="divide-y max-h-80 overflow-y-auto">
                {isLoading ? (
                  <div className="flex justify-center items-center py-8">
                    <div className="text-center">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-blue-500" />
                      <p className="text-sm text-gray-500">
                        {searchSource === 'elasticsearch'
                          ? 'Enhanced search in progress...'
                          : 'Searching...'}
                      </p>
                    </div>
                  </div>
                ) : searchResults.length > 0 ? (
                  searchResults.map((result, index) => (
                    <button
                      key={result.id}
                      onClick={() => {
                        handleSearchResultClick(result);
                      }}
                      className="block w-full text-left px-4 py-4 hover:bg-gray-50 transition cursor-pointer"
                    >
                      <div className="space-y-2">
                        <h4
                          className={cn(
                            'font-medium text-gray-900 line-clamp-2',
                            searchSource === 'elasticsearch' &&
                            'enhanced-search'
                          )}
                          dangerouslySetInnerHTML={createHighlightProps(
                            isStoryResult(result)
                              ? (result as any).titleHighlighted ||
                              result.title ||
                              'Untitled Story'
                              : (result as any).questionTitleHighlighted ||
                              (result as QuestionInfoType).questionTitle
                          )}
                        />
                        {(() => {
                          const descriptionHtml = isStoryResult(result)
                            ? result.content || result.theme || result.outcome
                            : (result as QuestionInfoType).questionDescription;

                          const descriptionPreview =
                            buildDescriptionPreview(descriptionHtml);

                          return (
                            descriptionPreview && (
                              <p
                                className={cn(
                                  'text-sm text-gray-600 line-clamp-2',
                                  searchSource === 'elasticsearch' &&
                                  'enhanced-search'
                                )}
                              >
                                {descriptionPreview}
                              </p>
                            )
                          );
                        })()}
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          {/* Content type indicator */}
                          {isStoryResult(result) ? (
                            <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-medium">
                              Story
                            </span>
                          ) : (
                            <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs font-medium">
                              Question
                            </span>
                          )}

                          {result.category?.name && (
                            <span className="bg-gray-100 px-2 py-1 rounded">
                              {result.category.name}
                            </span>
                          )}

                          {/* Show story count for questions */}
                          {!isStoryResult(result) &&
                            (result as QuestionInfoType).storyCount !==
                            undefined &&
                            (result as QuestionInfoType).storyCount! > 0 && (
                              <span>
                                {(result as QuestionInfoType).storyCount}{' '}
                                stories
                              </span>
                            )}

                          {/* Show question title for stories */}
                          {isStoryResult(result) && result.questionTitle && (
                            <span>From: {result.questionTitle}</span>
                          )}
                        </div>
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="flex justify-center items-center py-8">
                    <div className="text-center">
                      <p className="text-gray-500 mb-2">No results found</p>
                      <p className="text-xs text-gray-400">
                        {searchSource === 'elasticsearch'
                          ? 'Enhanced search found no matches'
                          : 'Try different keywords'}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="p-4 flex gap-3">
                <Button
                  onClick={() => {
                    router.push(`/search?q=${searchValue}`);
                    setIsSearchOpen(false);
                    setPopoverOpen(false);
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  View all results
                </Button>
                <Button
                  onClick={() => {
                    router.push('/ask-question');
                    setIsSearchOpen(false);
                    setPopoverOpen(false);
                  }}
                  variant="outline"
                  className="flex items-center gap-2 font-semibold text-ihs-blue border-ihs-blue hover:bg-ihs-blue/10"
                >
                  <PlusCircle className="w-4 h-4 text-ihs-blue" />
                  Add Question
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
